import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { useAuthStore } from '@/stores/auth.store';
import { getActiveTenantId } from '@/lib/activeTenant';
import {
  REQUEST_ID_HEADER,
  TRACEPARENT_HEADER,
  createRequestId,
  createTraceContext,
  isTraceparentOwnedExternally,
} from '@/lib/telemetry/trace-context';
import {
  ApiError,
  SESSION_EXPIRED_MESSAGE,
  normalizeError,
  telemetryFromRequest,
} from './errors';
import { authEndpoints } from './endpoints';
import { recoverSession, redirectToLogin } from './session';

// ─── Constants ────────────────────────────────────────────────────────────────

export const TIMEOUT_MS = 30_000;

// ─── Telemetry header injector ───────────────────────────────────────────────

/**
 * Correlate every request with the backend's logs and traces
 * (docs/observability/DESIGN.md §4–5):
 *   - `x-request-id`: a fresh UUID v4. The backend honours it, stamps it on
 *     every log line for the request and echoes it back; `ApiError.requestId`
 *     carries it to the error toast.
 *   - `traceparent`: a W3C trace context — but ONLY while CloudWatch RUM is not
 *     injecting its own. RUM's XHR plugin sets the header at `send()` time and
 *     XHR combines duplicate values, so two writers would produce an unparsable
 *     header. See `lib/monitoring.ts` for the ownership rule.
 * A caller that already set either header keeps its value (retries, tests).
 * Shared by the authenticated clients and `publicClient`.
 */
export function injectTelemetryHeaders(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  if (!config.headers.has(REQUEST_ID_HEADER)) {
    config.headers.set(REQUEST_ID_HEADER, createRequestId());
  }
  if (!isTraceparentOwnedExternally() && !config.headers.has(TRACEPARENT_HEADER)) {
    config.headers.set(TRACEPARENT_HEADER, createTraceContext().traceparent);
  }
  return config;
}

// ─── Auth header injectors ───────────────────────────────────────────────────

function injectOperationsAuthHeaders(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  const authState = useAuthStore.getState();
  const token = authState.token;
  const tenantId = getActiveTenantId();
  // Public auth flows resolve their subject/tenant from credentials in the
  // request body. Never let a previous browser session influence them.
  const isPublicAuth = isPublicAuthEndpoint(config.url);
  if (token && !isPublicAuth) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  if (tenantId && !isPublicAuth) {
    config.headers.set('x-tenant-id', tenantId);
  }
  return config;
}

// ─── Public auth endpoints (skip 401 redirect) ──────────────────────────────

const PUBLIC_AUTH_ENDPOINTS = [
  authEndpoints.login(),
  authEndpoints.refresh(),
  authEndpoints.requestPasswordReset(),
  authEndpoints.validateResetToken(),
  authEndpoints.resetPassword(),
];

// Only the token-addressed, unauthenticated invite routes are public:
// GET /auth/invites/:token, POST /auth/invites/:token/accept, and .../claim.
// Protected subpaths (e.g. POST /auth/invites/:inviteId/revoke) and the create
// route must keep the active session, so match those shapes exactly rather than
// treating the whole /auth/invites subtree as public.
const PUBLIC_INVITE_PATTERN = /^\/auth\/invites\/[^/]+(?:\/(?:accept|claim))?$/;

function isPublicAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  return PUBLIC_AUTH_ENDPOINTS.includes(url) || PUBLIC_INVITE_PATTERN.test(url);
}

/** The access token a request was sent with, as the server saw it. */
function bearerOf(config: InternalAxiosRequestConfig): string | null {
  const header = config.headers.get('Authorization');
  return typeof header === 'string' && header.startsWith('Bearer ')
    ? header.slice('Bearer '.length)
    : null;
}

// ─── Operations/microservices interceptors ────────────────────────────────────

function attachOperationsInterceptors(instance: AxiosInstance): AxiosInstance {
  instance.interceptors.request.use(injectTelemetryHeaders, (error) =>
    Promise.reject(normalizeError(error)),
  );
  instance.interceptors.request.use(injectOperationsAuthHeaders, (error) =>
    Promise.reject(normalizeError(error)),
  );

  instance.interceptors.response.use(
    (response) => {
      const serverVersion = response.headers['x-permissions-version'];
      if (serverVersion) {
        const version = Number(serverVersion);
        const store = useAuthStore.getState();
        if (!Number.isNaN(version) && version !== store.permissionsVersion) {
          // Silently refresh permissions in background
          refreshPermissionsFromServer();
        }
      }
      return response;
    },
    async (error) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      if (!error.response || error.response.status !== 401 || originalRequest._retry) {
        return Promise.reject(normalizeError(error));
      }

      originalRequest._retry = true;

      // Public auth endpoints — surface error directly (bad credentials, not expired session)
      if (isPublicAuthEndpoint(originalRequest.url)) {
        return Promise.reject(normalizeError(error));
      }

      // Operations tokens live 8 hours. Before giving up, find one that works:
      // a newer one this tab or another already holds, or a fresh one from
      // the refresh token. Then send the request again, once (`_retry`).
      const rejected = bearerOf(originalRequest);
      const fresh = await recoverSession(rejected);
      if (fresh) {
        originalRequest.headers.set('Authorization', `Bearer ${fresh}`);
        return instance(originalRequest);
      }

      redirectToLogin();
      return Promise.reject(
        new ApiError(
          401,
          'UNAUTHORIZED',
          SESSION_EXPIRED_MESSAGE,
          undefined,
          telemetryFromRequest(originalRequest, error.response),
        ),
      );
    },
  );

  return instance;
}

// ─── Services client factory ──────────────────────────────────────────────────

export function createServicesClient(baseURL: string | undefined): AxiosInstance {
  return attachOperationsInterceptors(
    axios.create({
      baseURL: baseURL ?? '',
      timeout: TIMEOUT_MS,
      headers: { 'Content-Type': 'application/json' },
    }),
  );
}

/** Operations / pricebook / tax / scheduler microservice client (VITE_OPERATIONS_URL) */
export const operationsClient: AxiosInstance = createServicesClient(
  import.meta.env.VITE_OPERATIONS_URL,
);

// ─── Silent permissions refresh ──────────────────────────────────────────────

let permissionsRefreshInFlight = false;

async function refreshPermissionsFromServer() {
  if (permissionsRefreshInFlight) return;
  permissionsRefreshInFlight = true;
  try {
    const res = await operationsClient.get<{
      roles: string[];
      permissions: string[];
      permissionsVersion: number;
      permissionScopes?: Record<string, string>;
      capabilityFlags?: Record<string, boolean>;
      fieldMasks?: string[];
      technicianId?: string | null;
    }>('/auth/permissions');
    useAuthStore.getState().refreshPermissions({
      permissions: res.data.permissions,
      permissionsVersion: res.data.permissionsVersion,
      permissionScopes: res.data.permissionScopes,
      capabilityFlags: res.data.capabilityFlags,
      fieldMasks: res.data.fieldMasks,
      technicianId: res.data.technicianId,
    });
  } catch {
    // Ignore — next response will trigger another attempt
  } finally {
    permissionsRefreshInFlight = false;
  }
}

/** @deprecated Scheduler routes are now served by the operations service. Use operationsClient directly. */
export const schedulerClient: AxiosInstance = operationsClient;

/** AI Ops microservice client (VITE_AI_OPS_URL) */
export const aiOpsClient: AxiosInstance = createServicesClient(import.meta.env.VITE_AI_OPS_URL);

// ─── Typed wrapper functions ───────────────────────────────────────────────────

export async function apiGet<T>(
  client: AxiosInstance,
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await client.get<T>(url, config);
  return res.data;
}

export async function apiPost<T, D = unknown>(
  client: AxiosInstance,
  url: string,
  data?: D,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await client.post<T>(url, data, config);
  return res.data;
}

export async function apiPut<T, D = unknown>(
  client: AxiosInstance,
  url: string,
  data?: D,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await client.put<T>(url, data, config);
  return res.data;
}

export async function apiPatch<T, D = unknown>(
  client: AxiosInstance,
  url: string,
  data?: D,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await client.patch<T>(url, data, config);
  return res.data;
}

export async function apiDelete<T = void>(
  client: AxiosInstance,
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await client.delete<T>(url, config);
  return res.data;
}
